/**
 * lib/reportProfile.ts
 * ─────────────────────
 * Per-customer report profile (column set) for the on-screen report surfaces —
 * the frontend mirror of backend report_profiles.get_profile, which drives the
 * Excel export. Matching is the same: case-insensitive customer-name substring
 * of the warehouse name (e.g. "DHL-Oatside" → oatside).
 *
 *  reckitt (default) : Detected QR Code · Description · Description WMS ·
 *                      SKU Batch Number WMS · Item Prefix · Product Status
 *  oatside           : License Plate · Description · License Plate WMS ·
 *                      Description WMS · Item WMS
 *                      (barcode-less site — no SKU Batch / Item Prefix /
 *                      Product Status columns; the WMS pallet code is the
 *                      License Plate WMS = expected_sku, Item WMS = item_code)
 *  fonterra          : the customer "Final Master inventory report" format —
 *                      identical to oatside (License Plate · Description ·
 *                      License Plate WMS · Description WMS · Item WMS · Image)
 *                      EXCEPT the count column header is "SOH Count" instead of
 *                      "Expected Count". Fonterra's pallet identity is the SSCC
 *                      QR (Oatside data model), not Reckitt's 1D barcode.
 */

export type ReportProfile = 'reckitt' | 'oatside' | 'fonterra' | 'nbc' | 'ceva';

export function reportProfileFor(warehouseName: string | null | undefined): ReportProfile {
  const n = warehouseName ?? '';
  if (/advantest/i.test(n)) return 'nbc';
  if (/ceva/i.test(n)) return 'ceva';
  if (/fonterra/i.test(n)) return 'fonterra';
  if (/oatside/i.test(n)) return 'oatside';
  return 'reckitt';
}

/**
 * True for profiles that use the Oatside-family column LAYOUT (Detected-code +
 * WMS-code + Item WMS + inline Image, no SKU-Batch/Item-Prefix/Product-Status).
 * Oatside and Fonterra differ only in the two identity-column header labels
 * (see identityHeaders); Reckitt has its own distinct column set.
 */
export function usesOatsideLayout(p: ReportProfile): boolean {
  return p === 'oatside' || p === 'fonterra';
}

export function usesNbcLayout(p: ReportProfile): boolean {
  // CEVA shares the Advantest NBC layout (same data model — identity +
  // occupancy); only the two identity-column headers differ. See
  // nbcIdentityHeaders.
  return p === 'nbc' || p === 'ceva';
}

/**
 * @deprecated CEVA now uses the full NBC layout (usesNbcLayout covers it).
 * Retained returning false so any residual guards are harmless dead branches.
 */
export function usesCevaPlaceholderLayout(_p: ReportProfile): boolean {
  return false;
}

/**
 * Identity-column headers for the NBC layout. Advantest's pallet identity is an
 * LPN; CEVA's is a GS1 SSCC (or PO number) — mirror of backend _NBC_CEVA.
 */
export function nbcIdentityHeaders(p: ReportProfile): { detected: string; wms: string } {
  if (p === 'ceva') return { detected: 'Pallet SSCC', wms: 'SSCC SOH' };
  return { detected: 'LPN Detected', wms: 'LPN SOH' };
}

/**
 * Header labels for the two identity columns in the oatside-family layout: the
 * drone-detected code and its WMS counterpart. Oatside and Fonterra both frame
 * the pallet identity as a License Plate (Oatside LPN / Fonterra SSCC QR).
 */
export function identityHeaders(_p: ReportProfile): { detected: string; wms: string } {
  return { detected: 'License Plate', wms: 'License Plate WMS' };
}

/**
 * The expected-quantity column header. Fonterra's customer format labels this
 * "SOH Count" (stock-on-hand); Oatside/Reckitt use "Expected Count".
 */
export function countHeader(p: ReportProfile): string {
  if (p === 'nbc') return 'OH SOH';
  return p === 'fonterra' ? 'SOH Count' : 'Expected Count';
}

/**
 * Display value for the "License Plate WMS" column. The reconcile engine writes
 * the sentinel "UNKNOWN" (with no count) when a location is absent from the
 * pinned SOH — i.e. it isn't in the WMS at all. Render that (and null) as a
 * clear "Not in WMS" instead of the raw "UNKNOWN", which reads as a data error.
 */
export function wmsPlateDisplay(expectedSku: string | null | undefined): string {
  if (!expectedSku || expectedSku === 'UNKNOWN') return 'Not in WMS';
  return expectedSku;
}
